import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {Popover, Space} from 'antd';
import {useDispatch} from 'react-redux';
import {
  StyledSubscriptionDetailsPanel,
  StyledSubscriptionDetailsPanelHeader,
  StyledSubscriptionDetailsPanelEditButton
} from './StyledSubscriptionDetailsPanel';
import BudgetDetailsForm from '../BudgetDetailsForm';
import OverflowProgress from '../../../OverflowProgress';
import BudgetPreviewLegend from '../../InvoicePage/BudgetPreviewLegend';
import {budgetHelpers, subscriptionsHelpers} from '../../../../utils/helpers';
import {subscriptionFormFields} from '../../../../constants';
import {colorsStyles} from '../../../../utils/styles';
import {StyledBudgetPreviewOverBudgetTag} from '../../InvoicePage/BudgetPreview/StyledBudgetPreview';
import {expenseActions} from '../../../../state/actions/expense.actions';

const budgetSpendingKeys = {
  EXPENSE: 'expense',
  REMAINING: 'remaining',
  SPENT: 'spent',
}

export const budgetSpendingData = {
  [budgetSpendingKeys.EXPENSE]: {
    amountKey: 'expense',
    color: '#B37FEB',
    labelKey: 'This expense',
  },
  [budgetSpendingKeys.SPENT]: {
    amountKey: 'spent',
    color: '#515A6B',
    labelKey: 'spent',
  },
  [budgetSpendingKeys.REMAINING]: {
    amountKey: 'remaining',
    color: colorsStyles.neutral_3,
    labelKey: 'remaining',
  },
}

const getBudgetDetails = ({expense, subscription}) => {
  const isExpenseInBilledPeriod = subscriptionsHelpers.isExpenseInBilledPeriod({expense, subscription});
  const budgetLimit = subscription[subscriptionFormFields.budgetLimitFieldName] || 0;
  const subscriptionSpent = subscriptionsHelpers.getSubscriptionTotalPayment(subscription) || 0;
  const spent = isExpenseInBilledPeriod ? subscriptionSpent : 0;
  const totalSpent = subscriptionSpent;

  return { budgetLimit, spent, subscriptionSpent, totalSpent };
}

const SubscriptionDetailsPanel = ({
  expense = null,
  onExpenseUpdate,
  onSubscriptionUpdate,
  subscription = null,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'subscriptions']);
  const dispatch = useDispatch();
  const [isOpenForm, setIsOpenForm] = useState(false);

  const spending = useMemo(() => {
    if (!expense || !subscription) return false;

    const { budgetLimit, expenseSpent, spent, totalSpent } = getBudgetDetails({expense, subscription});

    const utilizationPercentage = ((spent + expenseSpent) / (budgetLimit || 1)) * 100;

    const overBudget = utilizationPercentage > 100 ? Number(utilizationPercentage - 100).toFixed(2) : 0;

    return {
      expense: expenseSpent,
      spent: spent,
      remaining: budgetLimit - totalSpent,
      overBudget,
      utilizationPercentage,
    };
  }, [expense, subscription]);

  const { progressProps } = useMemo(() => {
    if (!expense || !subscription) return { progressProps: {} };

    const { budgetLimit, spent, totalSpent } = getBudgetDetails({expense, subscription});

    return budgetHelpers.calculateBudgetProgress({
      totalSpent,
      budgetLimit,
      spent,
    });
  }, [expense, subscription]);

  const renderTitle = (key, showEmpty = true) => {
    const data = budgetSpendingData[key];
    if (!data) return null;
    const amount = spending?.[data.amountKey] || 0;
    if (!showEmpty && !amount) return null;

    return <BudgetPreviewLegend amount={amount} color={data?.color} title={t(data?.labelKey)} />;
  }

  const recalculateExpensePolicyStatus = () => {
    if (expense?.id) {
      dispatch(expenseActions.recalculateExpensePolicyStatus({
        id: expense.id,
        successCallback: onExpenseUpdate
      }));
    }
  }

  const handleCloseForm = () => setIsOpenForm(false);

  const handleOpenForm = () => setIsOpenForm(true);

  const handleSuccessSubscriptionUpdate = (updatedSubscription) => {
    onSubscriptionUpdate?.(updatedSubscription);
    recalculateExpensePolicyStatus();
    handleCloseForm();
  }

  return (
    <StyledSubscriptionDetailsPanel {...rest}>
      <Space className='w-100' size='small' direction='vertical'>
        <StyledSubscriptionDetailsPanelHeader>

          {subscriptionsHelpers.getSubscriptionNameWithLink(subscription)}

          <Space size='small' align='center' className='d-flex-child'>
            <span>{subscriptionsHelpers.getBudgetBilledValue({t, data: subscription})}</span>

            <Popover
              placement='bottomLeft'
              content={(
                <BudgetDetailsForm
                  onCancel={handleCloseForm}
                  onSuccess={handleSuccessSubscriptionUpdate}
                  subscription={subscription}
                />
              )}
              open={isOpenForm}
            >
              <StyledSubscriptionDetailsPanelEditButton onClick={handleOpenForm} />
            </Popover>
          </Space>

        </StyledSubscriptionDetailsPanelHeader>

        <OverflowProgress
          {...progressProps}
          hidePercent={true}
          percent={spending.overBudget ? spending.utilizationPercentage : progressProps.percent}
          strokeColor={budgetSpendingData[budgetSpendingKeys.EXPENSE].color}
          size={{ height: 6 }}
          showInfo={false}
          trailColor={colorsStyles.neutral_3}
          strokeLinecap='butt'
        />

        <Space size='small' wrap>
          {renderTitle(budgetSpendingKeys.SPENT)}
          {renderTitle(budgetSpendingKeys.EXPENSE, false)}
          {renderTitle(budgetSpendingKeys.REMAINING)}
          {Boolean(spending.overBudget) && (
            <StyledBudgetPreviewOverBudgetTag>
              {t('overbudget')} {spending.overBudget}%
            </StyledBudgetPreviewOverBudgetTag>
          )}
        </Space>

      </Space>
    </StyledSubscriptionDetailsPanel>
  );
}

SubscriptionDetailsPanel.propTypes = {
  expense: PropTypes.object,
  onExpenseUpdate: PropTypes.func,
  onSubscriptionUpdate: PropTypes.func,
  subscription: PropTypes.object,
}

export default SubscriptionDetailsPanel;
